import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AsyncStage } from '../reduxCommon';
import {
	CompanyExternalUser,
	CompanyExternalUsers,
} from '../models/companyExternalUser';
import findIndex from 'lodash/findIndex';

interface CompanyExternalUserState {
	stage: AsyncStage;
	error: string;
	companyExternalUser: CompanyExternalUser;
	companyExternalUsers: CompanyExternalUsers;
}

const initialState: CompanyExternalUserState = {
	stage: AsyncStage.NoOp,
	error: '',
	companyExternalUser: {
		id: '',
		created: '',
		updated: '',
		firstName: '',
		lastName: '',
		email: '',
		phone: '',
		role: '',
		status: '',
		notes: '',
		lastContactDate: '',
		companyId: '',
	},
	companyExternalUsers: {
		list: [],
		count: 0,
		filter: {
			searchText: '',
			page: 0,
			take: 50,
			order: 'created',
			direction: 'desc',
			where: {},
		},
		initialized: false,
	},
};

const companyExternalUserSlice = createSlice({
	name: 'companyExternalUser',
	initialState,
	reducers: {
		asyncCallStart: (state: CompanyExternalUserState) => {
			state.stage = AsyncStage.Busy;
		},
		asyncCallDone: (state: CompanyExternalUserState) => {
			state.stage = AsyncStage.NoOp;
		},
		asyncCallError: (
			state: CompanyExternalUserState,
			action: PayloadAction<any>
		) => {
			state.stage = AsyncStage.Error;
			state.error = action.payload.message || 'UNKNOWN';
		},
		fetchCompanyExternalUserSuccess: (
			state: CompanyExternalUserState,
			action: PayloadAction<CompanyExternalUser>
		) => {
			state.companyExternalUser = action.payload;
			state.stage = AsyncStage.Success;
		},
		fetchCompanyExternalUsersSuccess: (
			state: CompanyExternalUserState,
			action: PayloadAction<{
				list: CompanyExternalUser[];
				count: number;
			}>
		) => {
			state.companyExternalUsers = {
				...state.companyExternalUsers,
				list: action.payload.list,
				count: action.payload.count,
				initialized: true,
			};
			state.stage = AsyncStage.Success;
		},
		createCompanyExternalUserSuccess: (
			state: CompanyExternalUserState,
			action: PayloadAction<CompanyExternalUser>
		) => {
			state.companyExternalUsers = {
				...state.companyExternalUsers,
				list: [action.payload, ...state.companyExternalUsers.list],
				count: state.companyExternalUsers.count + 1,
			};
			state.stage = AsyncStage.Success;
		},
		updateCompanyExternalUserSuccess: (
			state: CompanyExternalUserState,
			action: PayloadAction<CompanyExternalUser>
		) => {
			const list = [...state.companyExternalUsers.list];
			const user = action.payload;
			const userIndex = findIndex(list, (u) => u.id === user.id);
			if (userIndex >= 0) {
				list[userIndex] = user;
			}
			state.companyExternalUsers = {
				...state.companyExternalUsers,
				list,
			};
			if (state.companyExternalUser.id === user.id) {
				state.companyExternalUser = user;
			}
			state.stage = AsyncStage.Success;
		},
		updateCompanyExternalUsersFilter: (
			state: CompanyExternalUserState,
			action: PayloadAction<any>
		) => {
			state.companyExternalUsers = {
				...state.companyExternalUsers,
				filter: {
					...state.companyExternalUsers.filter,
					...action.payload,
				},
			};
		},
	},
});

export const {
	asyncCallStart,
	asyncCallDone,
	asyncCallError,
	fetchCompanyExternalUserSuccess,
	fetchCompanyExternalUsersSuccess,
	createCompanyExternalUserSuccess,
	updateCompanyExternalUserSuccess,
	updateCompanyExternalUsersFilter,
} = companyExternalUserSlice.actions;

export default companyExternalUserSlice.reducer;
